import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';
import { canViewActivityTracking, ROLES } from '../config/rbac';
import logger from '../utils/logger';

const ActivityTrackingContext = createContext();

export function useActivityTracking() {
  const context = useContext(ActivityTrackingContext);
  if (!context) {
    return null;
  }
  return context;
}

export function ActivityTrackingProvider({ children }) {
  const { currentUser } = useAuth();
  const { userRole, allUsers } = useRBAC();
  const [trackedUsers, setTrackedUsers] = useState([]); // Users/Moderators with their status
  const [userActivities, setUserActivities] = useState([]);
  const [allUsersActivities, setAllUsersActivities] = useState([]);
  const [activitySummary, setActivitySummary] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Track page duration
  const pageStartTime = useRef(null);
  const currentPageRef = useRef(null);
  const currentActionRef = useRef('Browsing');
  const heartbeatInterval = useRef(null);

  // Check if user should be tracked (only moderators - users are blocked from login)
  const shouldTrackUser = useCallback((role) => {
    // Note: ROLES.USER cannot login (blocked in RBACContext), so only track MODERATOR
    return role === ROLES.MODERATOR;
  }, []);

  // Check if current user can view tracking (admin/developer only)
  const canViewTracking = useCallback(() => {
    return canViewActivityTracking(currentUser?.email, userRole);
  }, [currentUser, userRole]);

  // Initialize and update presence
  useEffect(() => {
    logger.log('[ActivityTracking] Presence effect running:', {
      email: currentUser?.email,
      userRole: userRole,
      shouldTrack: shouldTrackUser(userRole),
      MODERATOR_ROLE: ROLES.MODERATOR
    });
    
    if (!currentUser?.email) {
      logger.log('[ActivityTracking] No email, skipping');
      return;
    }
    // Only track moderators (users are blocked from login in RBACContext)
    if (!shouldTrackUser(userRole)) {
      logger.log('[ActivityTracking] Skipping tracking for role:', userRole, '(expected: moderator)');
      return;
    }

    logger.log('[ActivityTracking] ✅ Starting presence tracking for:', currentUser.email);

    // Update presence on load
    const updatePresence = async () => {
      try {
        const presenceData = {
          user_email: currentUser.email,
          status: 'online',
          current_page: currentPageRef.current || 'dashboard',
          current_action: currentActionRef.current,
          last_seen: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        logger.log('[ActivityTracking] Updating presence:', presenceData);
        
        const { data, error } = await supabase
          .from('user_presence')
          .upsert(presenceData, { onConflict: 'user_email' })
          .select();
        
        logger.log('[ActivityTracking] Presence result - data:', data, 'error:', error);
        
        if (error) {
          logger.error('[ActivityTracking] ❌ Presence update error:', error.message, error.details, error.hint);
        } else {
          logger.log('[ActivityTracking] ✅ Presence updated successfully');
        }
      } catch (err) {
        logger.error('[ActivityTracking] Presence exception:', err);
      }
    };

    updatePresence();

    // Heartbeat every 30 seconds
    heartbeatInterval.current = setInterval(updatePresence, 30000);

    // Set offline on unload
    const handleUnload = async () => {
      try {
        await supabase.from('user_presence').upsert({
          user_email: currentUser.email,
          status: 'offline',
          last_seen: new Date().toISOString()
        }, { onConflict: 'user_email' });
      } catch (err) {}
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(heartbeatInterval.current);
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, [currentUser, userRole, shouldTrackUser]);

  // Auto-track login when user/moderator loads the app
  const hasTrackedLogin = useRef(false);
  useEffect(() => {
    logger.log('[ActivityTracking] Login effect running:', {
      email: currentUser?.email,
      userRole: userRole,
      hasTrackedLogin: hasTrackedLogin.current,
      shouldTrack: shouldTrackUser(userRole)
    });
    
    if (!currentUser?.email || !userRole) {
      logger.log('[ActivityTracking] No email or role, skipping login track');
      return;
    }
    if (!shouldTrackUser(userRole)) {
      logger.log('[ActivityTracking] Not a tracked role, skipping login track');
      return;
    }
    if (hasTrackedLogin.current) {
      logger.log('[ActivityTracking] Already tracked login, skipping');
      return;
    }
    
    hasTrackedLogin.current = true;
    logger.log('[ActivityTracking] Auto-tracking login for:', currentUser.email);
    
    // Insert login activity
    const trackLoginNow = async () => {
      try {
        const insertData = {
          user_email: currentUser.email,
          event_type: 'login',
          event_description: 'User logged in',
          page: 'app_load',
          metadata: { 
            login_time: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        };
        logger.log('[ActivityTracking] Inserting login data:', insertData);
        const { data, error } = await supabase.from('user_activity').insert([insertData]).select();
        logger.log('[ActivityTracking] Insert result - data:', data, 'error:', error);
        
        if (error) {
          logger.error('[ActivityTracking] ❌ Auto-login track error:', error.message, error.details, error.hint, error);
        } else {
          logger.log('[ActivityTracking] ✅ Login tracked successfully, data:', data);
        }
      } catch (err) {
        logger.error('[ActivityTracking] Auto-login exception:', err);
      }
    };
    
    trackLoginNow();
  }, [currentUser, userRole, shouldTrackUser]);

  // Track activity event
  const trackActivity = useCallback(async (eventType, eventDescription, page = null, metadata = {}) => {
    if (!currentUser?.email) return;
    
    // Only track moderators
    if (!shouldTrackUser(userRole)) return;

    try {
      const activityData = {
        user_email: currentUser.email,
        event_type: eventType,
        event_description: eventDescription,
        page: page || currentPageRef.current || 'unknown',
        metadata: metadata, // Pass object directly, not stringified
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        screen_width: window.innerWidth,
        screen_height: window.innerHeight
      };

      const { data, error } = await supabase
        .from('user_activity')
        .insert([activityData])
        .select();

      if (error) {
        logger.error('[ActivityTracking] Insert error:', error.message, error);
      } else {
        logger.log('[ActivityTracking] Tracked:', eventType, '-', eventDescription, 'data:', data);
      }
    } catch (err) {
      logger.error('[ActivityTracking] Exception:', err);
    }
  }, [currentUser, userRole, shouldTrackUser]);

  // Track page visit with duration
  const trackPageVisit = useCallback((pageName) => {
    // End previous page tracking
    if (currentPageRef.current && pageStartTime.current) {
      const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
      trackActivity('page_leave', `Left page: ${currentPageRef.current}`, currentPageRef.current, { duration_seconds: duration });
    }

    // Start new page tracking
    currentPageRef.current = pageName;
    pageStartTime.current = Date.now();
    trackActivity('page_visit', `Visited page: ${pageName}`, pageName);
  }, [trackActivity]);

  // Track button click
  const trackButtonClick = useCallback((buttonName, page = null) => {
    trackActivity('button_click', `Clicked: ${buttonName}`, page, { button_name: buttonName });
  }, [trackActivity]);

  // Track feature usage
  const trackFeatureUsage = useCallback((featureName, details = {}) => {
    trackActivity('feature_usage', `Used feature: ${featureName}`, null, { feature: featureName, ...details });
  }, [trackActivity]);

  // Track login
  const trackLogin = useCallback(() => {
    trackActivity('login', 'User logged in', null, { 
      login_time: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }, [trackActivity]);

  // Track logout
  const trackLogout = useCallback(() => {
    // Calculate total session duration
    const sessionDuration = pageStartTime.current ? Math.round((Date.now() - pageStartTime.current) / 1000) : 0;
    trackActivity('logout', 'User logged out', null, { session_duration_seconds: sessionDuration });
  }, [trackActivity]);

  // Track registration
  const trackRegistration = useCallback(() => {
    trackActivity('registration', 'User registered', null, { registration_time: new Date().toISOString() });
  }, [trackActivity]);

  // Set current action (for real-time tracking)
  const setCurrentAction = useCallback((action) => {
    currentActionRef.current = action;
    // Update presence immediately if tracked user
    if (currentUser?.email && shouldTrackUser(userRole)) {
      supabase.from('user_presence').upsert({
        user_email: currentUser.email,
        current_action: action,
        current_page: currentPageRef.current,
        last_seen: new Date().toISOString()
      }, { onConflict: 'user_email' }).catch(() => {});
    }
  }, [currentUser, userRole, shouldTrackUser]);

  // Fetch tracked users with their status (moderators only - users are blocked from login)
  const fetchTrackedUsers = useCallback(async () => {
    if (!currentUser?.email || !canViewActivityTracking(currentUser.email, userRole)) {
      logger.log('[ActivityTracking] fetchTrackedUsers: Access denied or no user');
      return;
    }

    logger.log('[ActivityTracking] fetchTrackedUsers: Starting...');
    setLoading(true);
    try {
      // DIRECTLY fetch moderators from database (don't rely on allUsers state)
      const { data: usersData, error: usersError } = await supabase
        .from('users_roles')
        .select('*')
        .eq('role', 'moderator');
      
      if (usersError) {
        logger.error('[ActivityTracking] Error fetching users_roles:', usersError);
      }
      
      const usersToTrack = usersData || [];
      logger.log('[ActivityTracking] Moderators to track:', usersToTrack.length, usersToTrack.map(u => u.email));

      if (usersToTrack.length === 0) {
        logger.log('[ActivityTracking] No moderators found to track');
        setTrackedUsers([]);
        setLoading(false);
        return;
      }

      // Get presence data for ALL users (not filtered)
      const { data: presenceData, error: presenceError } = await supabase
        .from('user_presence')
        .select('*');
      
      if (presenceError) {
        logger.error('[ActivityTracking] Error fetching presence:', presenceError);
      }
      logger.log('[ActivityTracking] Presence data:', presenceData?.length || 0, 'records');

      // Get last activity for tracked users
      const userEmails = usersToTrack.map(u => u.email);
      const { data: lastActivityData, error: activityError } = await supabase
        .from('user_activity')
        .select('user_email, event_type, timestamp')
        .in('user_email', userEmails)
        .order('timestamp', { ascending: false })
        .limit(5000);
      
      if (activityError) {
        logger.error('[ActivityTracking] Error fetching activities:', activityError);
      }
      logger.log('[ActivityTracking] Activity data:', lastActivityData?.length || 0, 'records');

      // Combine data
      const trackedUsersData = usersToTrack.map(user => {
        const presence = presenceData?.find(p => p.user_email === user.email);
        const userActivities = lastActivityData?.filter(a => a.user_email === user.email) || [];
        const lastLogin = userActivities.find(a => a.event_type === 'login');
        const lastLogout = userActivities.find(a => a.event_type === 'logout');
        const lastActivity = userActivities[0];

        // Determine if online (last seen within 2 minutes)
        const isOnline = presence?.last_seen && 
          (Date.now() - new Date(presence.last_seen).getTime()) < 120000;

        return {
          ...user,
          status: isOnline ? 'online' : 'offline',
          currentPage: presence?.current_page || null,
          currentAction: presence?.current_action || null,
          lastSeen: presence?.last_seen || lastActivity?.timestamp || null,
          lastLogin: lastLogin?.timestamp || null,
          lastLogout: lastLogout?.timestamp || null,
          hasActivityData: userActivities.length > 0,
          hasPresenceData: !!presence
        };
      });

      logger.log('[ActivityTracking] Tracked users result:', trackedUsersData);
      setTrackedUsers(trackedUsersData);
    } catch (err) {
      logger.error('[ActivityTracking] Error fetching tracked users:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole]);

  // Fetch user activities (for admin/developer)
  const fetchUserActivities = useCallback(async (userEmail = null) => {
    if (!currentUser?.email) return;
    
    // Only admin/developer can view all activities
    if (!canViewActivityTracking(currentUser.email, userRole)) return;

    setLoading(true);
    try {
      let query = supabase
        .from('user_activity')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(1000);

      if (userEmail) {
        query = query.eq('user_email', userEmail);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (userEmail) {
        setUserActivities(data || []);
      } else {
        setAllUsersActivities(data || []);
      }
    } catch (err) {
      logger.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole]);

  // Fetch monthly statistics (for admin/developer)
  const fetchMonthlyStats = useCallback(async () => {
    if (!currentUser?.email || !canViewActivityTracking(currentUser.email, userRole)) return;

    setLoading(true);
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      // Fetch all activities for current month
      const { data: monthlyData, error } = await supabase
        .from('user_activity')
        .select('*')
        .gte('timestamp', startOfMonth)
        .lte('timestamp', endOfMonth)
        .order('timestamp', { ascending: false });

      if (error) throw error;

      // Calculate statistics
      const activities = monthlyData || [];
      
      // Unique users
      const uniqueUsers = [...new Set(activities.map(a => a.user_email))];
      
      // Total logins
      const logins = activities.filter(a => a.event_type === 'login');
      
      // Page visits with duration
      const pageVisits = activities.filter(a => a.event_type === 'page_visit');
      const pageLeaves = activities.filter(a => a.event_type === 'page_leave');
      
      // Page stats
      const pageStats = {};
      pageVisits.forEach(visit => {
        if (!pageStats[visit.page]) {
          pageStats[visit.page] = { visits: 0, totalDuration: 0 };
        }
        pageStats[visit.page].visits++;
      });
      
      pageLeaves.forEach(leave => {
        if (pageStats[leave.page]) {
          const metadata = typeof leave.metadata === 'string' ? JSON.parse(leave.metadata || '{}') : (leave.metadata || {});
          pageStats[leave.page].totalDuration += metadata.duration_seconds || 0;
        }
      });

      // Feature usage
      const featureUsage = activities.filter(a => a.event_type === 'feature_usage');
      const featureStats = {};
      featureUsage.forEach(f => {
        const metadata = typeof f.metadata === 'string' ? JSON.parse(f.metadata || '{}') : (f.metadata || {});
        const feature = metadata.feature || 'unknown';
        featureStats[feature] = (featureStats[feature] || 0) + 1;
      });

      // Most active users
      const userActivityCount = {};
      activities.forEach(a => {
        userActivityCount[a.user_email] = (userActivityCount[a.user_email] || 0) + 1;
      });
      const sortedUsers = Object.entries(userActivityCount)
        .sort((a, b) => b[1] - a[1]);

      // Daily activity breakdown
      const dailyActivity = {};
      activities.forEach(a => {
        const day = a.timestamp.split('T')[0];
        dailyActivity[day] = (dailyActivity[day] || 0) + 1;
      });

      // Button clicks
      const buttonClicks = activities.filter(a => a.event_type === 'button_click').length;

      setMonthlyStats({
        totalActiveUsers: uniqueUsers.length,
        totalLogins: logins.length,
        totalPageVisits: pageVisits.length,
        totalButtonClicks: buttonClicks,
        totalActivities: activities.length,
        pageStats,
        featureStats,
        mostActiveUsers: sortedUsers.slice(0, 10),
        leastActiveUsers: sortedUsers.slice(-10).reverse(),
        dailyActivity,
        uniqueUsers,
        monthStart: startOfMonth,
        monthEnd: endOfMonth
      });
    } catch (err) {
      logger.error('Error fetching monthly stats:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole]);

  // Get user activity summary
  const getUserActivitySummary = useCallback(async (userEmail) => {
    if (!currentUser?.email || !canViewActivityTracking(currentUser.email, userRole)) return null;

    try {
      const { data, error } = await supabase
        .from('user_activity')
        .select('*')
        .eq('user_email', userEmail)
        .order('timestamp', { ascending: false });

      if (error) throw error;

      const activities = data || [];
      
      // Calculate summary
      const logins = activities.filter(a => a.event_type === 'login');
      const pageVisits = activities.filter(a => a.event_type === 'page_visit');
      const pageLeaves = activities.filter(a => a.event_type === 'page_leave');
      
      // Total time spent
      let totalTimeSpent = 0;
      pageLeaves.forEach(leave => {
        const metadata = typeof leave.metadata === 'string' ? JSON.parse(leave.metadata || '{}') : (leave.metadata || {});
        totalTimeSpent += metadata.duration_seconds || 0;
      });

      // Time per page
      const timePerPage = {};
      pageLeaves.forEach(leave => {
        const metadata = typeof leave.metadata === 'string' ? JSON.parse(leave.metadata || '{}') : (leave.metadata || {});
        if (!timePerPage[leave.page]) {
          timePerPage[leave.page] = 0;
        }
        timePerPage[leave.page] += metadata.duration_seconds || 0;
      });

      // Feature usage
      const featureUsage = activities.filter(a => a.event_type === 'feature_usage');

      return {
        email: userEmail,
        totalLogins: logins.length,
        lastLogin: logins[0]?.timestamp || null,
        firstActivity: activities[activities.length - 1]?.timestamp || null,
        totalPageVisits: pageVisits.length,
        totalTimeSpentSeconds: totalTimeSpent,
        timePerPage,
        featureUsageCount: featureUsage.length,
        recentActivities: activities.slice(0, 50),
        totalActivities: activities.length
      };
    } catch (err) {
      logger.error('Error getting user summary:', err);
      return null;
    }
  }, [currentUser, userRole]);

  // Export activities to CSV
  const exportToCSV = useCallback((activities, filename = 'user_activities.csv') => {
    if (!activities || activities.length === 0) return;

    const headers = ['User Email', 'Event Type', 'Description', 'Page', 'Timestamp', 'Metadata'];
    const rows = activities.map(a => [
      a.user_email,
      a.event_type,
      a.event_description,
      a.page || '',
      a.timestamp,
      a.metadata || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }, []);

  // Track on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPageRef.current && pageStartTime.current) {
        const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
        // Use sendBeacon for reliable tracking on page unload
        const data = JSON.stringify({
          user_email: currentUser?.email,
          event_type: 'page_leave',
          event_description: `Left page: ${currentPageRef.current}`,
          page: currentPageRef.current,
          metadata: JSON.stringify({ duration_seconds: duration }),
          timestamp: new Date().toISOString()
        });
        navigator.sendBeacon?.(`${supabase.supabaseUrl}/rest/v1/user_activity`, data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentUser]);

  // Get detailed user activity (for popup when clicking on user)
  const getDetailedUserActivity = useCallback(async (userEmail) => {
    if (!currentUser?.email || !canViewActivityTracking(currentUser.email, userRole)) return null;

    try {
      // Get all activities for this user
      const { data: activities, error } = await supabase
        .from('user_activity')
        .select('*')
        .eq('user_email', userEmail)
        .order('timestamp', { ascending: false })
        .limit(500);

      if (error) throw error;

      const allActivities = activities || [];
      
      // Get presence data
      const { data: presenceData } = await supabase
        .from('user_presence')
        .select('*')
        .eq('user_email', userEmail)
        .single();

      // Calculate detailed stats
      const logins = allActivities.filter(a => a.event_type === 'login');
      const logouts = allActivities.filter(a => a.event_type === 'logout');
      const pageVisits = allActivities.filter(a => a.event_type === 'page_visit');
      const pageLeaves = allActivities.filter(a => a.event_type === 'page_leave');
      const buttonClicks = allActivities.filter(a => a.event_type === 'button_click');
      const featureUsage = allActivities.filter(a => a.event_type === 'feature_usage');
      const registration = allActivities.find(a => a.event_type === 'registration');

      // Page visit counts
      const pageVisitCounts = {};
      pageVisits.forEach(visit => {
        pageVisitCounts[visit.page] = (pageVisitCounts[visit.page] || 0) + 1;
      });

      // Time spent per page
      const timePerPage = {};
      pageLeaves.forEach(leave => {
        const metadata = typeof leave.metadata === 'string' ? JSON.parse(leave.metadata || '{}') : (leave.metadata || {});
        const duration = metadata.duration_seconds || 0;
        timePerPage[leave.page] = (timePerPage[leave.page] || 0) + duration;
      });

      // Most visited pages (sorted)
      const mostVisitedPages = Object.entries(pageVisitCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([page, count]) => ({
          page,
          visits: count,
          timeSpent: timePerPage[page] || 0
        }));

      // Total time spent
      const totalTimeSpent = Object.values(timePerPage).reduce((sum, t) => sum + t, 0);

      // Is currently online?
      const isOnline = presenceData?.last_seen && 
        (Date.now() - new Date(presenceData.last_seen).getTime()) < 120000;

      return {
        email: userEmail,
        // Status
        status: isOnline ? 'online' : 'offline',
        currentPage: presenceData?.current_page || null,
        currentAction: presenceData?.current_action || null,
        lastSeen: presenceData?.last_seen || null,
        
        // Login/Logout info
        lastLogin: logins[0]?.timestamp || null,
        lastLogout: logouts[0]?.timestamp || null,
        totalLogins: logins.length,
        registeredAt: registration?.timestamp || null,
        
        // Page activity
        totalPageVisits: pageVisits.length,
        mostVisitedPages,
        timePerPage,
        totalTimeSpentSeconds: totalTimeSpent,
        
        // Actions
        totalButtonClicks: buttonClicks.length,
        totalFeatureUsage: featureUsage.length,
        
        // Recent activities (last 30)
        recentActivities: allActivities.slice(0, 30).map(a => ({
          type: a.event_type,
          description: a.event_description,
          page: a.page,
          timestamp: a.timestamp,
          metadata: typeof a.metadata === 'string' ? JSON.parse(a.metadata || '{}') : (a.metadata || {})
        })),
        
        // All activities count
        totalActivities: allActivities.length
      };
    } catch (err) {
      logger.error('Error getting detailed user activity:', err);
      return null;
    }
  }, [currentUser, userRole]);

  // Fetch activity summary for admin panel overview
  const fetchActivitySummary = useCallback(async () => {
    if (!currentUser?.email || !canViewActivityTracking(currentUser.email, userRole)) return;

    setLoading(true);
    try {
      // DIRECTLY fetch moderators from database
      const { data: usersData } = await supabase
        .from('users_roles')
        .select('*')
        .eq('role', 'moderator');
      
      const usersToTrack = usersData || [];

      if (usersToTrack.length === 0) {
        setActivitySummary({ totalUsers: 0, onlineUsers: 0, mostVisitedPages: [], recentLogins: [] });
        setLoading(false);
        return;
      }

      // Get presence for online status
      const { data: presenceData } = await supabase
        .from('user_presence')
        .select('*')
        .in('user_email', usersToTrack.map(u => u.email));

      // Get recent activities
      const { data: recentActivities } = await supabase
        .from('user_activity')
        .select('*')
        .in('user_email', usersToTrack.map(u => u.email))
        .order('timestamp', { ascending: false })
        .limit(500);

      const activities = recentActivities || [];

      // Count online users
      const onlineUsers = (presenceData || []).filter(p => 
        Date.now() - new Date(p.last_seen).getTime() < 120000
      ).length;

      // Most visited pages
      const pageVisits = activities.filter(a => a.event_type === 'page_visit');
      const pageCounts = {};
      pageVisits.forEach(v => {
        pageCounts[v.page] = (pageCounts[v.page] || 0) + 1;
      });
      const mostVisitedPages = Object.entries(pageCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([page, count]) => ({ page, count }));

      // Recent logins
      const recentLogins = activities
        .filter(a => a.event_type === 'login')
        .slice(0, 10)
        .map(a => ({
          email: a.user_email,
          timestamp: a.timestamp
        }));

      // Most active users
      const userActivityCounts = {};
      activities.forEach(a => {
        userActivityCounts[a.user_email] = (userActivityCounts[a.user_email] || 0) + 1;
      });
      const mostActiveUsers = Object.entries(userActivityCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([email, count]) => ({ email, activityCount: count }));

      setActivitySummary({
        totalUsers: usersToTrack.length,
        onlineUsers,
        mostVisitedPages,
        recentLogins,
        mostActiveUsers,
        totalActivities: activities.length
      });
    } catch (err) {
      logger.error('Error fetching activity summary:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole, allUsers]);

  const value = {
    // Tracking functions
    trackActivity,
    trackPageVisit,
    trackButtonClick,
    trackFeatureUsage,
    trackLogin,
    trackLogout,
    trackRegistration,
    setCurrentAction,
    
    // Admin functions
    fetchUserActivities,
    fetchMonthlyStats,
    getUserActivitySummary,
    getDetailedUserActivity,
    fetchTrackedUsers,
    fetchActivitySummary,
    exportToCSV,
    canViewTracking,
    shouldTrackUser,
    
    // State
    userActivities,
    allUsersActivities,
    trackedUsers,
    activitySummary,
    monthlyStats,
    loading
  };

  return (
    <ActivityTrackingContext.Provider value={value}>
      {children}
    </ActivityTrackingContext.Provider>
  );
}

export default ActivityTrackingContext;
